import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import styled from 'styled-components';
import { FUTURE_TOKENS } from '../../constants/futureTokens.js';
import { getDropShadowByElevation } from '../../styles/elevation.js';
import { spacing2x, spacing1x, spacing3x } from '../../styles/spacing.js';
import { getAdornment } from '../../utils/adornment.js';
import { isEmpty } from '../../utils/lang.js';
import { ButtonMenu } from '../ButtonMenu/ButtonMenu.js';
import { ContentCardCheckbox } from '../ContentCard/ContentCardCheckbox/ContentCardCheckbox.js';
import { AdornmentContainer } from '../ContentChipBase/AdornmentContainer.js';
import { ActionBtnGroup } from '../Entity/ActionBtnGroup.js';
import { IconHost } from '../IconHost/IconHost.js';
import { IconDrag } from '../Icons/IconDrag.js';
import { IconMore } from '../Icons/IconMore.js';
import { jsx, jsxs } from 'react/jsx-runtime';

var _excluded = ["actions", "buttonMenuProps", "checkboxProps", "dragHandleProps", "iconHostProps", "imageUrl", "isDraggable", "linkProps", "primaryActions", "secondaryActions", "startAdornment", "title"];
var DragIconContainer = styled.div.withConfig({
  displayName: "ContentChip__DragIconContainer",
  componentId: "sc-4vde32-0"
})(["&&{display:flex;align-items:center;height:100%;cursor:grab;}"]);
var ActionsContainer = styled.div.withConfig({
  displayName: "ContentChip__ActionsContainer",
  componentId: "sc-4vde32-1"
})(["&&{position:absolute;padding-inline-start:", ";inset-inline-end:", ";background:", ";height:100%;display:flex;align-items:center;}"], spacing2x, spacing2x, function (_ref) {
  var isSelected = _ref.isSelected;
  return isSelected ? FUTURE_TOKENS.CONTENT_CHIP_ACTION_CONTAINER_SELECTED_BACKGROUND_COLOR : FUTURE_TOKENS.CONTENT_CHIP_ACTION_CONTAINER_BACKGROUND_COLOR;
});
var ContentChipContainer = styled.article.withConfig({
  displayName: "ContentChip__ContentChipContainer",
  componentId: "sc-4vde32-2"
})(["&&{display:flex;align-items:center;justify-content:flex-start;background-color:", ";border-style:solid;border-color:", ";border-width:1px;border-radius:", ";height:64px;padding-block:0;padding-inline:0 ", ";position:relative;", ";}&& ", "{pointer-events:none;}&& ", ",&& ", "{opacity:0;width:0;}&& ", "{border-right:0;padding-inline:0;}&&:hover,&&:focus,&&:focus-within{box-shadow:", ";outline:none;}&&:hover ", ",&&:focus ", ",&&:focus-within ", "{pointer-events:unset;}&&:hover ", ",&&:hover ", ",&&:focus ", ",&&:focus ", ",&&:focus-within ", ",&&:focus-within ", "{opacity:1;width:auto;}&&:hover ", ",&&:focus ", ",&&:focus-within ", "{border-right:1px solid ", ";padding-inline:calc(", " - 2px);}"], function (_ref2) {
  var theme = _ref2.theme;
  return theme.neutralColors.white;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.neutralColors.silver;
}, spacing1x, spacing3x, function (_ref4) {
  var isSelected = _ref4.isSelected,
    theme = _ref4.theme;
  return isSelected ? "\n            border-color: ".concat(theme.CTAColors.primary, ";\n            background-color: #E6E6F4;\n    ") : void 0;
}, ActionsContainer, ActionsContainer, DragIconContainer, DragIconContainer, getDropShadowByElevation(2), ActionsContainer, ActionsContainer, ActionsContainer, ActionsContainer, DragIconContainer, ActionsContainer, DragIconContainer, ActionsContainer, DragIconContainer, DragIconContainer, DragIconContainer, DragIconContainer, function (_ref5) {
  var theme = _ref5.theme;
  return theme.neutralColors.silver;
}, spacing2x);
var StyledAnchor = styled.a.withConfig({
  displayName: "ContentChip__StyledAnchor",
  componentId: "sc-4vde32-3"
})(["&&{color:", ";cursor:pointer;}&&:hover,&&:focus,&&:active{color:", ";}"], function (_ref6) {
  var theme = _ref6.theme;
  return theme.neutralColors.charcoal;
}, function (_ref7) {
  var theme = _ref7.theme;
  return theme.neutralColors.charcoal;
});
var Content = styled.div.withConfig({
  displayName: "ContentChip__Content",
  componentId: "sc-4vde32-4"
})(["&&{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;line-height:18px;}"]);
var TitleContainer = styled.div.withConfig({
  displayName: "ContentChip__TitleContainer",
  componentId: "sc-4vde32-5"
})(["&&{display:flex;margin-inline-start:", ";align-items:center;}"], spacing2x);
var ContentContainer = styled.div.withConfig({
  displayName: "ContentChip__ContentContainer",
  componentId: "sc-4vde32-6"
})(["&&{display:flex;align-items:center;flex:1;}"]);
var StyledIconHost = styled(IconHost).withConfig({
  displayName: "ContentChip__StyledIconHost",
  componentId: "sc-4vde32-7"
})(["&&{margin-inline-end:", ";}"], spacing1x);
var ContentBackground = styled.div.withConfig({
  displayName: "ContentChip__ContentBackground",
  componentId: "sc-4vde32-8"
})(["&&{background-color:", ";background-image:url(", ");background-position-x:center;background-position-y:center;background-repeat:no-repeat;background-size:cover;height:100%;width:100%;inset-inline-start:0;position:absolute;inset-block-start:0;}"], function (_ref8) {
  var theme = _ref8.theme;
  return theme.neutralColors.silver;
}, function (_ref9) {
  var imageUrl = _ref9.imageUrl;
  return imageUrl;
});
var ContentCardOverlay = styled.div.withConfig({
  displayName: "ContentChip__ContentCardOverlay",
  componentId: "sc-4vde32-9"
})(["&&{height:100%;overflow:hidden;position:relative;width:114px;}"]);
var StyledDiv = styled.div.withConfig({
  displayName: "ContentChip__StyledDiv",
  componentId: "sc-4vde32-10"
})(["&&{height:100%;border-right:1px solid ", ";}"], function (_ref10) {
  var theme = _ref10.theme;
  return theme.neutralColors.silver;
});
var CheckboxContainer = styled.div.withConfig({
  displayName: "ContentChip__CheckboxContainer",
  componentId: "sc-4vde32-11"
})(["&&{display:flex;align-items:center;position:absolute;inset-block-start:", ";inset-inline-start:", ";}"], spacing2x, spacing2x);
var ContentChip = /*#__PURE__*/React.forwardRef(function ContentChip2(props, ref) {
  var _props$actions = props.actions,
    actions = _props$actions === void 0 ? [] : _props$actions,
    buttonMenuProps = props.buttonMenuProps,
    checkboxProps = props.checkboxProps,
    _props$dragHandleProp = props.dragHandleProps,
    dragHandleProps = _props$dragHandleProp === void 0 ? {} : _props$dragHandleProp,
    iconHostProps = props.iconHostProps,
    _props$imageUrl = props.imageUrl,
    imageUrl = _props$imageUrl === void 0 ? "" : _props$imageUrl,
    isDraggable = props.isDraggable,
    _props$linkProps = props.linkProps,
    linkProps = _props$linkProps === void 0 ? {} : _props$linkProps,
    _props$primaryActions = props.primaryActions,
    primaryActions = _props$primaryActions === void 0 ? [] : _props$primaryActions,
    secondaryActions = props.secondaryActions,
    startAdornment = props.startAdornment,
    title = props.title,
    rest = _objectWithoutProperties(props, _excluded);
  var selectable = !isEmpty(checkboxProps);
  var isSelected = checkboxProps === null || checkboxProps === void 0 ? void 0 : checkboxProps.checked;
  var areActionsEmpty = !primaryActions.length && isEmpty(secondaryActions);
  var isDragHandlePropsEmpty = isEmpty(dragHandleProps);
  var focusProps = isDragHandlePropsEmpty ? {
    tabIndex: 0
  } : {};
  var areActionsPresent = actions.length || buttonMenuProps;
  var startIcon = null;
  if (startAdornment) {
    startIcon = /*#__PURE__*/jsx(AdornmentContainer, {
      children: getAdornment(startAdornment, {
        size: 20
      })
    });
  } else if (!isEmpty(iconHostProps)) {
    startIcon = /*#__PURE__*/jsx(StyledIconHost, _objectSpread({
      size: 20
    }, iconHostProps));
  }
  return /*#__PURE__*/jsxs(ContentChipContainer, _objectSpread(_objectSpread(_objectSpread({
    ref: ref
  }, focusProps), {}, {
    isSelected: isSelected
  }, rest), {}, {
    children: [(isDraggable || !isDragHandlePropsEmpty) && /*#__PURE__*/jsx(DragIconContainer, _objectSpread(_objectSpread({}, dragHandleProps), {}, {
      children: /*#__PURE__*/jsx(IconDrag, {
        size: 20
      })
    })), /*#__PURE__*/jsx(StyledDiv, {
      children: /*#__PURE__*/jsxs(ContentCardOverlay, {
        children: [/*#__PURE__*/jsx(ContentBackground, {
          imageUrl: imageUrl
        }), selectable && /*#__PURE__*/jsx(CheckboxContainer, {
          children: /*#__PURE__*/jsx(ContentCardCheckbox, _objectSpread({}, checkboxProps))
        })]
      })
    }), /*#__PURE__*/jsx(ContentContainer, {
      children: /*#__PURE__*/jsx(TitleContainer, {
        children: /*#__PURE__*/jsxs(Content, {
          children: [startIcon, /*#__PURE__*/jsx(StyledAnchor, _objectSpread(_objectSpread({
            tabIndex: 0
          }, linkProps), {}, {
            children: title
          }))]
        })
      })
    }), (!areActionsEmpty || areActionsPresent) && /*#__PURE__*/jsxs(ActionsContainer, {
      isSelected: isSelected,
      children: [areActionsPresent ? null : /*#__PURE__*/jsx(ActionBtnGroup, {
        primaryActions: primaryActions,
        secondaryActions: secondaryActions
      }), actions.length ? React.Children.map(actions, function (child) {
        return /*#__PURE__*/jsx("div", {
          children: child
        });
      }) : null, buttonMenuProps && /*#__PURE__*/jsx("div", {
        children: /*#__PURE__*/jsx(ButtonMenu, _objectSpread(_objectSpread({}, buttonMenuProps), {}, {
          buttonProps: _objectSpread({
            hideLabel: true,
            startAdornment: IconMore,
            variant: "ghost"
          }, buttonMenuProps.buttonProps)
        }))
      })]
    })]
  }));
});
ContentChip.displayName = "ContentChip";

export { ContentChip };
